/**
 * View Transitions API for Next.js 16 App Router
 *
 * This is a Next.js 16 and React 19 compatible recreation of the next-view-transitions library.
 * It provides smooth page transitions using the browser's View Transitions API.
 *
 * Key differences from the original library:
 * - Removed blocking use() calls that trigger "Uncached data was accessed outside of <Suspense>" errors
 * - Uses effect-based coordination instead of suspending during transitions
 * - Fully compatible with Next.js 16's new caching model and React 19 Server Components
 *
 * Usage:
 *
 * 1. Wrap your app in the ViewTransitions component:
 * ```tsx
 * import { ViewTransitions } from '@/lib/view-transitions';
 *
 * export default function Layout({ children }) {
 *   return (
 *     <ViewTransitions>
 *       {children}
 *     </ViewTransitions>
 *   );
 * }
 * ```
 *
 * 2. Use the Link component for navigation with transitions:
 * ```tsx
 * import { Link } from '@/lib/view-transitions';
 *
 * export default function Page() {
 *   return <Link href="/about">About</Link>;
 * }
 * ```
 *
 * 3. Or use the useTransitionRouter hook for programmatic navigation:
 * ```tsx
 * 'use client';
 * import { useRouter } from '@/lib/view-transitions';
 *
 * export default function Page() {
 *   const router = useRouter();
 *   return <button onClick={() => router.push('/about')}>Go to About</button>;
 * }
 * ```
 *
 * 4. Add CSS for view transitions in your global styles:
 * ```css
 * @keyframes fade-in {
 *   from { opacity: 0; }
 * }
 *
 * @keyframes fade-out {
 *   to { opacity: 0; }
 * }
 *
 * ::view-transition-old(root) {
 *   animation: 90ms cubic-bezier(0.4, 0, 1, 1) both fade-out;
 * }
 *
 * ::view-transition-new(root) {
 *   animation: 210ms cubic-bezier(0, 0, 0.2, 1) 90ms both fade-in;
 * }
 * ```
 */

export { ViewTransitions } from "./view-transitions";
export { Link } from "./link";
export { useRouter } from "./use-router";
export type {
  TransitionRouter,
  TransitionOptions,
  NavigateOptionsWithTransition,
} from "./types";
export type { LinkProps } from "./link";
